import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import { Button } from 'react-bootstrap';
import { Cell } from 'react-table';

import { useAppSelector } from 'hooks';
import { Table } from 'components/UI';
import { getInterventions } from 'store/groups/reducer';
import { UsersColumnView } from 'pages/Dashboard/Common/Table';

interface TasksListProps {
  tasks: ITask[];
  truncate?: boolean;
  carePlanTemplate?: ICarePlanTemplate;
}

const TasksList = ({ tasks, truncate, carePlanTemplate }: TasksListProps) => {
  truncate = truncate && tasks.length > 2;
  const { formatMessage } = useIntl();
  const interventions = useAppSelector(getInterventions);
  const [expand, setExpand] = useState<boolean>(false);

  const getInterventionById = (id: string) => {
    return (
      interventions.managed.find((intervention) => intervention.id === id) ||
      Object.values(interventions.userCreated)
        .flat()
        .find((intervention: any) => intervention.id === id)
    );
  };

  const columns = [
    {
      id: 'status',
      accessor: 'status',
      Header: () => formatMessage({ id: 'status' }),
      Cell: ({ row: { original } }: Cell<ITask>) =>
        formatMessage({ id: `tasks.status.${original.status}` }),
      minWidth: 100,
      hide: !!carePlanTemplate
    },
    {
      id: 'interventionId',
      accessor: 'interventionId',
      Header: () => formatMessage({ id: 'titleKey' }),
      Cell: ({ row: { original } }: Cell<ITask>) => {
        const intervention = getInterventionById(original.interventionId);
        return (
          <div className="text-truncate">
            {intervention?.name ? formatMessage({ id: intervention.name }) : '—'}
          </div>
        );
      },
      minWidth: 100
    },
    {
      id: 'patient',
      accessor: 'patientInfo.lastName',
      Header: () => formatMessage({ id: 'patientKey' }),
      Cell: ({ row: { original } }: Cell<ITask>) => (
        <>{original.patientInfo.lastName + ', ' + original.patientInfo.firstName}</>
      ),
      minWidth: 100,
      hide: !!carePlanTemplate
    },
    {
      id: 'assignees',
      accessor: 'assignees',
      Header: () => formatMessage({ id: 'taskAssigneesKey' }),
      Cell: ({ row: { original } }: Cell<ITask>) => (
        <UsersColumnView admins={original.assignees} row={original} />
      ),
      minWidth: 200,
      align: 'center'
    }
  ];

  return (
    <>
      <div
        className={`table table-layout-fixed bg-white ${
          truncate ? ' overflow-hidden' : ' overflow-auto'
        }  mb-1`}
        style={{ maxHeight: truncate ? (expand ? 'none' : '15vh') : '30vh' }}
        data-test="closed-tasks-table"
      >
        {truncate && !expand && <div className="fade-overlay-white" />}
        <Table dataTestIdPrefix="closed" data={tasks} columns={columns} hideMargin />
      </div>
      {truncate && (
        <div className="d-flex justify-content-center">
          <Button
            onClick={() => setExpand(!expand)}
            variant="link"
            data-test={`tasks-list-${expand ? 'collapse' : 'expand'}-btn`}
          >
            {formatMessage({ id: expand ? 'hideKey' : 'showAllClosedTasksKey' })}
          </Button>
        </div>
      )}
    </>
  );
};

export default TasksList;
